<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <h2>{{ flowId ? 'Edit Flow' : 'Create Flow' }}</h2>
        <button @click="closeModal" class="close-btn">&times;</button>
      </div>

      <div class="modal-body">
        <div class="builder-layout">
          <!-- Left Sidebar - Components Palette -->
          <div class="components-palette">
            <h3>Components</h3>

            <div class="component-category">
              <h4>Screens</h4>
              <div
                v-for="screen in screenComponents"
                :key="screen.type"
                class="component-item"
                draggable="true"
                @dragstart="handleDragStart($event, screen)"
              >
                <span class="component-icon">{{ screen.icon }}</span>
                <span class="component-name">{{ screen.label }}</span>
              </div>
            </div>

            <div class="component-category">
              <h4>Form Elements</h4>
              <div
                v-for="element in formElements"
                :key="element.type"
                class="component-item"
                draggable="true"
                @dragstart="handleDragStart($event, element)"
              >
                <span class="component-icon">{{ element.icon }}</span>
                <span class="component-name">{{ element.label }}</span>
              </div>
            </div>

            <div class="component-category">
              <h4>Display Elements</h4>
              <div
                v-for="element in displayElements"
                :key="element.type"
                class="component-item"
                draggable="true"
                @dragstart="handleDragStart($event, element)"
              >
                <span class="component-icon">{{ element.icon }}</span>
                <span class="component-name">{{ element.label }}</span>
              </div>
            </div>
          </div>

          <!-- Center - Canvas -->
          <div class="canvas-area">
            <div class="canvas-toolbar">
              <button @click="addScreen" class="btn-add-screen">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                Add Screen
              </button>
              <button @click="previewFlow" class="btn-preview">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
                Preview
              </button>
              <button @click="viewJson" class="btn-json">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                </svg>
                View JSON
              </button>
            </div>

            <div
              class="canvas-content"
              @drop="handleDrop"
              @dragover.prevent
            >
              <div v-if="flow.screens.length === 0" class="empty-canvas">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5.04-6.71l-2.75 3.54-1.96-2.36L6.5 17h11l-3.54-4.71z"/>
                </svg>
                <p>Drag components here to start building your flow</p>
              </div>

              <div v-else class="screens-container">
                <div
                  v-for="(screen, index) in flow.screens"
                  :key="screen.id"
                  class="screen-card"
                  :class="{ 'active': selectedScreen === screen.id }"
                  @click="selectScreen(screen.id)"
                >
                  <div class="screen-header">
                    <span class="screen-title">{{ screen.title || `Screen ${index + 1}` }}</span>
                    <div class="screen-actions">
                      <button @click.stop="moveScreenUp(index)" :disabled="index === 0" class="btn-icon">↑</button>
                      <button @click.stop="moveScreenDown(index)" :disabled="index === flow.screens.length - 1" class="btn-icon">↓</button>
                      <button @click.stop="deleteScreen(index)" class="btn-icon btn-delete">×</button>
                    </div>
                  </div>
                  <div class="screen-body">
                    <div
                      v-for="(component, cIndex) in screen.layout.children"
                      :key="cIndex"
                      class="component-preview"
                    >
                      <strong>{{ component.type }}:</strong> {{ component.label || component.text || 'Component' }}
                    </div>
                    <div v-if="screen.layout.children.length === 0" class="empty-screen">
                      Drop components here
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Live Preview - Phone -->
          <div class="preview-panel">
            <WhatsAppFlowPreview
              :screen-title="currentScreen ? currentScreen.title : 'Demo Screen'"
              :components="currentScreen ? currentScreen.layout.children : []"
            />
          </div>

          <!-- Right Sidebar - Properties -->
          <div class="properties-panel">
            <h3>Properties</h3>

            <div v-if="!selectedScreen" class="no-selection">
              <p>Select a screen to edit properties</p>
            </div>

            <div v-else class="properties-form">
              <div class="form-group">
                <label>Screen ID</label>
                <input
                  v-model="currentScreen.id"
                  type="text"
                  class="form-control"
                  @input="validateScreenId"
                >
              </div>

              <div class="form-group">
                <label>Screen Title</label>
                <input
                  v-model="currentScreen.title"
                  type="text"
                  class="form-control"
                  placeholder="Screen title..."
                >
              </div>

              <div class="form-group">
                <label>Terminal</label>
                <input
                  v-model="currentScreen.terminal"
                  type="checkbox"
                  class="form-checkbox"
                >
                <small>Is this the final screen?</small>
              </div>

              <div class="form-group">
                <label>Refresh On Back</label>
                <input
                  v-model="currentScreen.refresh_on_back"
                  type="checkbox"
                  class="form-checkbox"
                >
              </div>

              <!-- Data Key removed - WhatsApp doesn't use this field -->
              <!-- <div class="form-group">
                <label>Data Key (optional)</label>
                <input
                  v-model="currentScreen.data.key"
                  type="text"
                  class="form-control"
                  placeholder="data_key"
                >
              </div> -->

              <hr>

              <h4>Components</h4>
              <div
                v-for="(component, index) in currentScreen.layout.children"
                :key="index"
                class="component-editor"
                draggable="true"
                @dragstart="handleComponentDragStart($event, index)"
                @dragover="handleComponentDragOver($event, index)"
                @drop="handleComponentDrop($event, index)"
                @dragend="handleComponentDragEnd"
              >
                <div class="component-editor-header">
                  <span class="drag-handle">⋮⋮</span>
                  <strong>{{ component.type }}</strong>
                  <div class="component-actions">
                    <button @click.stop="moveComponentUp(index)" :disabled="index === 0" class="btn-icon btn-sm" title="Move Up">↑</button>
                    <button @click.stop="moveComponentDown(index)" :disabled="index === currentScreen.layout.children.length - 1" class="btn-icon btn-sm" title="Move Down">↓</button>
                    <button @click="removeComponent(index)" class="btn-icon btn-delete">×</button>
                  </div>
                </div>

                <!-- Dynamic component properties -->
                <div class="component-properties">
                  <div v-if="component.label !== undefined" class="form-group">
                    <label>Label</label>
                    <input v-model="component.label" type="text" class="form-control">
                  </div>

                  <div v-if="component.name !== undefined" class="form-group">
                    <label>Name (Data Key)</label>
                    <input v-model="component.name" type="text" class="form-control">
                  </div>

                  <div v-if="component.required !== undefined" class="form-group">
                    <label>
                      <input v-model="component.required" type="checkbox" class="form-checkbox">
                      Required
                    </label>
                  </div>

                  <div v-if="component.text !== undefined" class="form-group">
                    <label>Text</label>
                    <textarea v-model="component.text" class="form-control" rows="3"></textarea>
                  </div>

                  <div v-if="component['input-type'] !== undefined" class="form-group">
                    <label>Input Type</label>
                    <select v-model="component['input-type']" class="form-control">
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="password">Password</option>
                    </select>
                  </div>

                  <div v-if="component['data-source'] !== undefined" class="form-group">
                    <div class="options-header">
                      <label>Options</label>
                      <button @click.stop="addOption(component)" class="btn-add-option" type="button">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                        </svg>
                        Add Option
                      </button>
                    </div>

                    <div class="options-list">
                      <div v-for="(option, optIndex) in component['data-source']" :key="optIndex" class="option-item">
                        <div class="option-drag-handle">⋮⋮</div>
                        <div class="option-fields">
                          <input
                            v-model="option.id"
                            type="text"
                            class="form-control-sm"
                            placeholder="ID (e.g., yes)"
                            @input="ensureArray(component, 'data-source')"
                          />
                          <input
                            v-model="option.title"
                            type="text"
                            class="form-control-sm"
                            placeholder="Title (e.g., Yes)"
                            @input="ensureArray(component, 'data-source')"
                          />
                        </div>
                        <button @click.stop="removeOption(component, optIndex)" class="btn-remove-option" type="button" title="Remove">×</button>
                      </div>

                      <div v-if="!component['data-source'] || component['data-source'].length === 0" class="empty-options">
                        <span>No options added yet. Click "Add Option" to create one.</span>
                      </div>
                    </div>

                    <div class="quick-options">
                      <small>Quick add:</small>
                      <button @click.stop="addQuickOptions(component, 'yesno')" class="btn-quick" type="button">Yes/No</button>
                      <button @click.stop="addQuickOptions(component, 'rating')" class="btn-quick" type="button">1-5 Rating</button>
                      <button @click.stop="addQuickOptions(component, 'satisfaction')" class="btn-quick" type="button">Satisfaction</button>
                    </div>
                  </div>

                  <div v-if="component['min-selected-items'] !== undefined" class="form-group">
                    <label>Min Selected Items</label>
                    <input v-model.number="component['min-selected-items']" type="number" min="0" class="form-control" @input="validateSelectionRange(component)">
                    <small v-if="showSelectionWarning(component)" class="validation-warning">
                      ⚠️ For single selection (min=max=1), use RadioButtonsGroup instead of CheckboxGroup
                    </small>
                  </div>

                  <div v-if="component['max-selected-items'] !== undefined" class="form-group">
                    <label>Max Selected Items</label>
                    <input v-model.number="component['max-selected-items']" type="number" min="0" class="form-control" @input="validateSelectionRange(component)">
                    <small v-if="showSelectionWarning(component)" class="validation-warning">
                      ⚠️ For single selection (min=max=1), use RadioButtonsGroup instead of CheckboxGroup
                    </small>
                  </div>

                  <div v-if="component['min-date'] !== undefined" class="form-group">
                    <label>Min Date (YYYY-MM-DD)</label>
                    <input v-model="component['min-date']" type="date" class="form-control">
                  </div>

                  <div v-if="component['max-date'] !== undefined" class="form-group">
                    <label>Max Date (YYYY-MM-DD)</label>
                    <input v-model="component['max-date']" type="date" class="form-control">
                  </div>

                  <div v-if="component.src !== undefined" class="form-group">
                    <label>Image URL or Asset ID</label>
                    <input v-model="component.src" type="text" class="form-control" placeholder="https://... or asset_id">
                  </div>

                  <div v-if="component['scale-type'] !== undefined" class="form-group">
                    <label>Scale Type</label>
                    <select v-model="component['scale-type']" class="form-control">
                      <option value="cover">Cover</option>
                      <option value="contain">Contain</option>
                      <option value="fit">Fit</option>
                    </select>
                  </div>

                  <div v-if="component['aspect-ratio'] !== undefined" class="form-group">
                    <label>Aspect Ratio</label>
                    <input v-model.number="component['aspect-ratio']" type="number" step="0.1" min="0.1" class="form-control" placeholder="1.0">
                    <small>Width/Height ratio (e.g., 1.0 for square, 1.78 for 16:9)</small>
                  </div>

                  <div v-if="component.visible !== undefined" class="form-group">
                    <label>
                      <input v-model="component.visible" type="checkbox" class="form-checkbox">
                      Visible
                    </label>
                  </div>

                  <!-- Footer-specific properties -->
                  <div v-if="component.type === 'Footer' && component['on-click-action']" class="form-group">
                    <label>Action Type</label>
                    <select v-model="component['on-click-action'].name" class="form-control" @change="updateFooterAction(component)">
                      <option value="navigate">Navigate (Go to next screen)</option>
                      <option value="complete">Complete (Submit & close flow)</option>
                    </select>
                    <small v-if="component['on-click-action'].name === 'complete'" style="color: #10b981;">
                      ✓ This will submit all form data to your endpoint and close the flow
                    </small>
                    <small v-if="component['on-click-action'].name === 'navigate'" style="color: #3b82f6;">
                      → This will navigate to the next screen
                    </small>
                  </div>

                  <div v-if="component.type === 'Footer' && component['on-click-action'] && component['on-click-action'].name === 'navigate'" class="form-group">
                    <label>Next Screen</label>
                    <select v-model="component['on-click-action'].next.name" class="form-control">
                      <option value="">-- Select screen --</option>
                      <option v-for="screen in flow.screens" :key="screen.id" :value="screen.id">
                        {{ screen.title || screen.id }}
                      </option>
                    </select>
                    <small>Select which screen to navigate to when button is clicked</small>
                  </div>

                  <div v-if="component.type === 'EmbeddedLink' && component['on-click-action']" class="form-group">
                    <label>Action Type</label>
                    <select v-model="component['on-click-action'].name" class="form-control">
                      <option value="navigate">Navigate</option>
                      <option value="complete">Complete</option>
                    </select>
                  </div>

                  <div v-if="component.type === 'EmbeddedLink' && component['on-click-action'] && component['on-click-action'].name === 'navigate'" class="form-group">
                    <label>Next Screen</label>
                    <select v-model="component['on-click-action'].next.name" class="form-control">
                      <option value="">-- Select screen --</option>
                      <option v-for="screen in flow.screens" :key="screen.id" :value="screen.id">
                        {{ screen.title || screen.id }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <button @click="addComponentToScreen" class="btn-add-component">
                + Add Component
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="saveFlow" class="btn btn-primary" :disabled="saving">
          {{ saving ? 'Saving...' : 'Save Flow' }}
        </button>
        <button @click="closeModal" class="btn btn-secondary">Cancel</button>
      </div>
    </div>

    <!-- JSON Viewer Modal -->
    <div v-if="showJsonViewer" class="json-viewer-overlay" @click.self="showJsonViewer = false">
      <div class="json-viewer-container">
        <div class="json-viewer-header">
          <h3>Flow JSON</h3>
          <button @click="copyJson" class="btn btn-sm">Copy</button>
          <button @click="showJsonViewer = false" class="close-btn">&times;</button>
        </div>
        <pre class="json-content">{{ JSON.stringify(generateFlowJson(), null, 2) }}</pre>
      </div>
    </div>
  </div>
</template>

<script>
import WhatsAppFlowPreview from './WhatsAppFlowPreview.vue'
import { AuthService } from '@/services/auth'

export default {
  name: 'FlowBuilderModal',
  components: {
    WhatsAppFlowPreview
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    flowId: {
      type: String,
      default: null
    },
    flowData: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      flow: {
        version: '6.1',
        data_api_version: '3.0',
        routing_model: {},
        screens: []
      },
      selectedScreen: null,
      draggedComponent: null,
      draggedComponentIndex: null,
      dragOverIndex: null,
      saving: false,
      showJsonViewer: false,
      screenComponents: [
        { type: 'screen', label: 'Screen', icon: '📱' }
      ],
      formElements: [
        { type: 'Form', label: 'Form Container', icon: '📋', template: { type: 'Form', name: 'form', children: [] }, description: 'Required for v<4.0 to wrap input fields' },
        { type: 'TextInput', label: 'Text Input', icon: '✏️', template: { type: 'TextInput', name: 'field', label: 'Enter text', required: false, 'input-type': 'text' } },
        { type: 'TextArea', label: 'Text Area', icon: '📝', template: { type: 'TextArea', name: 'field', label: 'Enter text', required: false } },
        { type: 'CheckboxGroup', label: 'Checkbox', icon: '☑️', template: { type: 'CheckboxGroup', name: 'field', label: 'Select options', required: false, 'data-source': [], 'min-selected-items': 0, 'max-selected-items': 10 } },
        { type: 'RadioButtonsGroup', label: 'Radio Buttons', icon: '🔘', template: { type: 'RadioButtonsGroup', name: 'field', label: 'Select one', required: false, 'data-source': [] } },
        { type: 'Dropdown', label: 'Dropdown', icon: '📋', template: { type: 'Dropdown', name: 'field', label: 'Select', required: false, 'data-source': [] } },
        { type: 'DatePicker', label: 'Date Picker', icon: '📅', template: { type: 'DatePicker', name: 'field', label: 'Select date', required: false, 'min-date': '', 'max-date': '' } },
        { type: 'OptIn', label: 'Opt-In', icon: '✓', template: { type: 'OptIn', name: 'field', label: 'I agree', required: false } },
        { type: 'PhotoPicker', label: 'Photo Picker', icon: '📷', template: { type: 'PhotoPicker', name: 'photo', label: 'Upload photo', required: false } },
        { type: 'DocumentPicker', label: 'Document Picker', icon: '📄', template: { type: 'DocumentPicker', name: 'document', label: 'Upload document', required: false } }
      ],
      displayElements: [
        { type: 'TextHeading', label: 'Heading', icon: 'H', template: { type: 'TextHeading', text: 'Heading Text' }, charLimit: 80 },
        { type: 'TextSubheading', label: 'Subheading', icon: 'h', template: { type: 'TextSubheading', text: 'Subheading Text' }, charLimit: 80 },
        { type: 'TextBody', label: 'Body Text', icon: 'T', template: { type: 'TextBody', text: 'Body text content' }, charLimit: 4096 },
        { type: 'TextCaption', label: 'Caption', icon: 't', template: { type: 'TextCaption', text: 'Caption text' }, charLimit: 4096 },
        { type: 'Image', label: 'Image', icon: '🖼️', template: { type: 'Image', src: '', 'scale-type': 'cover', 'aspect-ratio': 1 } },
        { type: 'Footer', label: 'Footer', icon: '⬇️', template: { type: 'Footer', label: 'Continue', 'on-click-action': { name: 'navigate', next: { type: 'screen', name: '' }, payload: {} } } },
        { type: 'EmbeddedLink', label: 'Embedded Link', icon: '🔗', template: { type: 'EmbeddedLink', text: 'Click here', 'on-click-action': { name: 'navigate', next: { type: 'screen', name: '' }, payload: {} } } }
      ],
      maxComponentsPerScreen: 50
    }
  },
  computed: {
    currentScreen () {
      if (!this.selectedScreen) return null
      return this.flow.screens.find(s => s.id === this.selectedScreen)
    }
  },
  watch: {
    isOpen (newVal) {
      if (newVal && this.flowData) {
        this.loadFlowData()
      } else if (newVal) {
        this.resetFlow()
      }
    }
  },
  methods: {
    async loadFlowData () {
      console.log('loadFlowData called with flowData:', this.flowData)

      // If flowData already has screens (flow JSON structure), use it directly
      if (this.flowData && this.flowData.screens) {
        this.flow = JSON.parse(JSON.stringify(this.flowData))
        if (this.flow.screens.length > 0) {
          this.selectedScreen = this.flow.screens[0].id
        }
        return
      }

      // For new flows (status = DRAFT, no json_version), start fresh instead of loading WhatsApp's template
      if (this.flowData && this.flowData.status === 'DRAFT' && !this.flowData.json_version) {
        console.log('New flow detected - starting with clean template instead of loading from WhatsApp')
        this.resetFlow()
        return
      }

      // Otherwise, we need to fetch the flow JSON from the backend
      if (this.flowId) {
        try {
          const token = AuthService.getAuthToken()
          const response = await fetch(`https://api.quali-d.com/whatsapp/flows/${this.flowId}/json`, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json'
            }
          })

          const data = await response.json()
          console.log('Flow JSON response:', data)

          if (data.success && data.flow_json) {
            const flowJson = data.flow_json

            // IMPORTANT: Override WhatsApp's version with our supported version
            // WhatsApp may return version 7.3, but we should use 6.1 for compatibility
            const version = '6.1'

            // Fix data types: convert arrays to objects where needed
            const routingModel = Array.isArray(flowJson.routing_model) ? {} : (flowJson.routing_model || {})

            // Fix screens: convert arrays to objects, validate screen IDs
            const screens = (flowJson.screens || []).map(screen => {
              // Fix screen ID if it contains invalid characters (numbers)
              const fixedId = screen.id.replace(/[^a-zA-Z_]/g, '_')
              return {
                ...screen,
                id: fixedId,
                data: Array.isArray(screen.data) ? {} : (screen.data || {})
              }
            })

            this.flow = {
              version: version,
              data_api_version: flowJson.data_api_version || '3.0',
              routing_model: routingModel,
              screens: screens
            }

            // Select first screen if available
            if (this.flow.screens.length > 0) {
              this.selectedScreen = this.flow.screens[0].id
            }

            console.log('Flow loaded successfully with', this.flow.screens.length, 'screens')
          } else if (response.status === 404) {
            console.log('Flow JSON not found yet - starting with empty flow')
            this.resetFlow()
          } else {
            console.error('Failed to load flow:', data.message)
            alert(`Failed to load flow: ${data.message}`)
            this.resetFlow()
          }
        } catch (error) {
          console.error('Error loading flow JSON:', error)
          alert('Failed to load flow content. Starting with empty flow.')
          this.resetFlow()
        }
      }
    },
    resetFlow () {
      this.flow = {
        version: '6.1',
        data_api_version: '3.0',
        routing_model: {},
        screens: []
      }
      this.selectedScreen = null
    },
    addScreen () {
      // WhatsApp requires screen IDs to only contain alphabets and underscores (no numbers!)
      const screenNumber = this.flow.screens.length + 1
      const screenId = `screen_${this.generateRandomId()}`
      const newScreen = {
        id: screenId,
        title: `Screen ${screenNumber}`,
        terminal: false,
        data: {},
        layout: {
          type: 'SingleColumnLayout',
          children: []
        }
      }

      this.flow.screens.push(newScreen)
      this.selectedScreen = screenId
    },
    generateRandomId () {
      // Generate a random ID with only letters and underscores (no numbers)
      const chars = 'abcdefghijklmnopqrstuvwxyz'
      let id = ''
      for (let i = 0; i < 8; i++) {
        id += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      return id
    },
    selectScreen (screenId) {
      this.selectedScreen = screenId
    },
    deleteScreen (index) {
      if (confirm('Are you sure you want to delete this screen?')) {
        const screen = this.flow.screens[index]
        if (this.selectedScreen === screen.id) {
          this.selectedScreen = null
        }
        this.flow.screens.splice(index, 1)
      }
    },
    moveScreenUp (index) {
      if (index > 0) {
        const temp = this.flow.screens[index]
        this.flow.screens[index] = this.flow.screens[index - 1]
        this.flow.screens[index - 1] = temp
        this.$forceUpdate()
      }
    },
    moveScreenDown (index) {
      if (index < this.flow.screens.length - 1) {
        const temp = this.flow.screens[index]
        this.flow.screens[index] = this.flow.screens[index + 1]
        this.flow.screens[index + 1] = temp
        this.$forceUpdate()
      }
    },
    handleDragStart (event, component) {
      this.draggedComponent = component
      event.dataTransfer.effectAllowed = 'copy'
    },
    handleDrop (event) {
      event.preventDefault()

      if (!this.draggedComponent) return

      if (this.draggedComponent.type === 'screen') {
        this.addScreen()
      } else if (this.selectedScreen && this.draggedComponent.template) {
        // Validate component limit
        if (this.currentScreen.layout.children.length >= this.maxComponentsPerScreen) {
          alert(`Maximum ${this.maxComponentsPerScreen} components per screen allowed`)
          this.draggedComponent = null
          return
        }

        const newComponent = JSON.parse(JSON.stringify(this.draggedComponent.template))
        this.currentScreen.layout.children.push(newComponent)
      }

      this.draggedComponent = null
    },
    removeComponent (index) {
      if (this.currentScreen) {
        this.currentScreen.layout.children.splice(index, 1)
      }
    },
    moveComponentUp (index) {
      if (index > 0 && this.currentScreen) {
        const components = this.currentScreen.layout.children
        const temp = components[index]
        components[index] = components[index - 1]
        components[index - 1] = temp
        this.$forceUpdate()
      }
    },
    moveComponentDown (index) {
      if (this.currentScreen && index < this.currentScreen.layout.children.length - 1) {
        const components = this.currentScreen.layout.children
        const temp = components[index]
        components[index] = components[index + 1]
        components[index + 1] = temp
        this.$forceUpdate()
      }
    },
    handleComponentDragStart (event, index) {
      this.draggedComponentIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.target.classList.add('dragging')
    },
    handleComponentDragOver (event, index) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
      this.dragOverIndex = index
    },
    handleComponentDrop (event, dropIndex) {
      event.preventDefault()
      event.stopPropagation()

      if (this.draggedComponentIndex === null || this.draggedComponentIndex === dropIndex) {
        return
      }

      if (!this.currentScreen) return

      const components = this.currentScreen.layout.children
      const draggedItem = components[this.draggedComponentIndex]

      // Remove from old position
      components.splice(this.draggedComponentIndex, 1)

      // Insert at new position
      const newIndex = this.draggedComponentIndex < dropIndex ? dropIndex - 1 : dropIndex
      components.splice(newIndex, 0, draggedItem)

      this.$forceUpdate()
    },
    handleComponentDragEnd (event) {
      event.target.classList.remove('dragging')
      this.draggedComponentIndex = null
      this.dragOverIndex = null
    },
    addComponentToScreen () {
      if (this.currentScreen) {
        this.currentScreen.layout.children.push({
          type: 'TextBody',
          text: 'New component'
        })
      }
    },
    validateScreenId () {
      // WhatsApp requires screen IDs to only contain alphabets and underscores (no numbers!)
      if (this.currentScreen) {
        this.currentScreen.id = this.currentScreen.id.replace(/[^a-zA-Z_]/g, '_')
      }
    },
    ensureArray (component, key) {
      if (!Array.isArray(component[key])) {
        component[key] = []
      }
      this.$forceUpdate()
    },
    addOption (component) {
      if (!component['data-source']) {
        component['data-source'] = []
      }
      component['data-source'].push({
        id: `option_${component['data-source'].length + 1}`,
        title: `Option ${component['data-source'].length + 1}`
      })
      this.$forceUpdate()
    },
    removeOption (component, index) {
      if (component['data-source'] && Array.isArray(component['data-source'])) {
        component['data-source'].splice(index, 1)
        this.$forceUpdate()
      }
    },
    addQuickOptions (component, type) {
      if (!component['data-source']) {
        component['data-source'] = []
      }

      const templates = {
        yesno: [
          { id: 'yes', title: 'Yes' },
          { id: 'no', title: 'No' }
        ],
        rating: [
          { id: '1', title: '1 - Poor' },
          { id: '2', title: '2 - Fair' },
          { id: '3', title: '3 - Good' },
          { id: '4', title: '4 - Very Good' },
          { id: '5', title: '5 - Excellent' }
        ],
        satisfaction: [
          { id: 'very_unsatisfied', title: 'Very Unsatisfied' },
          { id: 'unsatisfied', title: 'Unsatisfied' },
          { id: 'neutral', title: 'Neutral' },
          { id: 'satisfied', title: 'Satisfied' },
          { id: 'very_satisfied', title: 'Very Satisfied' }
        ]
      }

      if (templates[type]) {
        component['data-source'] = JSON.parse(JSON.stringify(templates[type]))

        // For rating template, suggest RadioButtonsGroup if this is a CheckboxGroup
        if (type === 'rating' && component.type === 'CheckboxGroup') {
          alert('💡 Tip: For single-selection ratings, use RadioButtonsGroup instead of CheckboxGroup.\n\nCheckboxGroup with min=max=1 will fail WhatsApp validation.')
        }

        this.$forceUpdate()
      }
    },
    validateSelectionRange (component) {
      // Validate that CheckboxGroup doesn't have min=max=1 (use RadioButtonsGroup instead)
      this.$forceUpdate()
    },
    showSelectionWarning (component) {
      return component.type === 'CheckboxGroup' &&
             component['min-selected-items'] === 1 &&
             component['max-selected-items'] === 1
    },
    updateFooterAction (component) {
      // When switching between navigate and complete, ensure proper structure
      if (component['on-click-action'].name === 'complete') {
        // Complete action still needs payload (empty object)
        delete component['on-click-action'].next
        component['on-click-action'].payload = {}
      } else if (component['on-click-action'].name === 'navigate') {
        // Add next/payload structure for navigate action
        if (!component['on-click-action'].next) {
          component['on-click-action'].next = { type: 'screen', name: '' }
        }
        if (!component['on-click-action'].payload) {
          component['on-click-action'].payload = {}
        }
      }
      this.$forceUpdate()
    },
    generateFlowJson () {
      console.log('generateFlowJson called')
      console.log('this.flow.routing_model IS Array?', Array.isArray(this.flow.routing_model))
      console.log('this.flow.screens[0].data IS Array?', this.flow.screens.length > 0 ? Array.isArray(this.flow.screens[0].data) : 'N/A')

      // FORCE routing_model to be an empty object (not array)
      const routingModel = {}

      // FORCE each screen's data to be an empty object (not array)
      const screens = this.flow.screens.map(screen => {
        const screenCopy = JSON.parse(JSON.stringify(screen))
        // Explicitly set data to empty object
        screenCopy.data = {}

        // Auto-detect terminal screens: If a screen has a Footer with "complete" action, mark it as terminal
        if (screenCopy.layout && screenCopy.layout.children) {
          const hasCompleteAction = screenCopy.layout.children.some(component =>
            component.type === 'Footer' &&
            component['on-click-action'] &&
            component['on-click-action'].name === 'complete'
          )
          if (hasCompleteAction) {
            screenCopy.terminal = true
          }
        }

        return screenCopy
      })

      const result = {
        version: this.flow.version,
        data_api_version: this.flow.data_api_version,
        routing_model: routingModel,
        screens: screens
      }

      console.log('Generated flow JSON:', JSON.stringify(result, null, 2))
      return result
    },
    viewJson () {
      this.showJsonViewer = true
    },
    copyJson () {
      const json = JSON.stringify(this.generateFlowJson(), null, 2)
      navigator.clipboard.writeText(json)
      alert('JSON copied to clipboard!')
    },
    previewFlow () {
      // Open preview in new window or modal
      alert('Preview functionality coming soon!')
    },
    validateFlow () {
      const errors = []

      // Check each screen for validation issues
      this.flow.screens.forEach((screen, screenIndex) => {
        screen.layout.children.forEach((component, componentIndex) => {
          // Check for CheckboxGroup with min=max=1
          if (component.type === 'CheckboxGroup' &&
              component['min-selected-items'] === 1 &&
              component['max-selected-items'] === 1) {
            errors.push(`Screen "${screen.title}" → Component ${componentIndex + 1} (CheckboxGroup): For single selection, use RadioButtonsGroup instead. CheckboxGroup with min=max=1 will fail WhatsApp validation.`)
          }
        })
      })

      return errors
    },
    async saveFlow () {
      this.saving = true
      try {
        // Validate flow before saving
        const validationErrors = this.validateFlow()
        if (validationErrors.length > 0) {
          const errorMessage = '⚠️ Flow Validation Errors:\n\n' + validationErrors.join('\n\n') + '\n\nPlease fix these issues before saving.'
          alert(errorMessage)
          this.saving = false
          return
        }

        const flowJson = this.generateFlowJson()
        const token = AuthService.getAuthToken()

        if (this.flowId) {
          // Update flow JSON schema
          const response = await fetch(`https://api.quali-d.com/whatsapp/flows/${this.flowId}/assets`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ flow_json: flowJson })
          })

          const data = await response.json()

          if (data.success) {
            this.$emit('flow-saved', data.data)
            this.closeModal()
          } else {
            alert('Error: ' + data.message)
          }
        } else {
          alert('Please create a flow first, then use the builder to edit it')
        }
      } catch (error) {
        console.error('Error saving flow:', error)
        alert('Error saving flow: ' + error.message)
      } finally {
        this.saving = false
      }
    },
    closeModal () {
      this.$emit('close')
      this.resetFlow()
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: var(--bg-primary);
  border-radius: 12px;
  width: 95vw;
  height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

.builder-layout {
  display: grid;
  grid-template-columns: 200px minmax(300px, 500px) 360px 1fr;
  height: 100%;
  gap: 0;
}

.components-palette,
.properties-panel,
.preview-panel {
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  padding: 20px;
  overflow-y: auto;
}

.preview-panel {
  padding: 0;
  overflow: hidden;
}

.properties-panel {
  border-right: none;
  padding: 20px;
}

.components-palette h3,
.properties-panel h3 {
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.component-category {
  margin-bottom: 24px;
}

.component-category h4 {
  margin: 0 0 12px 0;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.component-item {
  padding: 10px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: grab;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;
}

.component-item:hover {
  background: #f3f4f6;
  border-color: #25d366;
  transform: translateX(4px);
}

.component-item:active {
  cursor: grabbing;
}

.component-icon {
  font-size: 18px;
}

.component-name {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.canvas-area {
  display: flex;
  flex-direction: column;
  background: white;
}

.canvas-toolbar {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 12px;
}

.canvas-toolbar button {
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  background: white;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.canvas-toolbar button:hover {
  background: #f9fafb;
  border-color: #25d366;
}

.btn-add-screen {
  color: #25d366;
}

.canvas-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f9fafb;
}

.empty-canvas {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  text-align: center;
}

.empty-canvas p {
  margin-top: 16px;
  font-size: 14px;
}

.screens-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.screen-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
}

.screen-card:hover {
  border-color: #25d366;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.1);
}

.screen-card.active {
  border-color: #25d366;
  box-shadow: 0 4px 16px rgba(37, 211, 102, 0.2);
}

.screen-header {
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.screen-title {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
}

.screen-actions {
  display: flex;
  gap: 4px;
}

.btn-icon {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-icon:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #25d366;
}

.btn-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btn-delete {
  color: #ef4444;
  font-size: 20px;
  font-weight: bold;
}

.btn-delete:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

.screen-body {
  padding: 16px;
  min-height: 100px;
}

.component-preview {
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 13px;
}

.empty-screen {
  text-align: center;
  padding: 40px 20px;
  color: #9ca3af;
  font-size: 13px;
  border: 2px dashed #e5e7eb;
  border-radius: 6px;
}

.no-selection {
  text-align: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.properties-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.form-group small {
  font-size: 11px;
  color: #6b7280;
}

.form-group .validation-warning {
  color: #f59e0b;
  background: #fffbeb;
  padding: 6px 10px;
  border-radius: 4px;
  border-left: 3px solid #f59e0b;
  font-weight: 500;
  margin-top: 4px;
  display: block;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 13px;
  transition: all 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #25d366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.form-checkbox {
  width: 16px;
  height: 16px;
}

.component-editor {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
  cursor: grab;
  transition: all 0.2s;
}

.component-editor:hover {
  border-color: #25d366;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.1);
}

.component-editor.dragging {
  opacity: 0.5;
  cursor: grabbing;
  transform: scale(0.95);
}

.component-editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
  gap: 8px;
}

.drag-handle {
  cursor: grab;
  color: #9ca3af;
  font-size: 16px;
  line-height: 1;
  user-select: none;
}

.drag-handle:active {
  cursor: grabbing;
}

.component-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.btn-sm {
  width: 24px;
  height: 24px;
  font-size: 12px;
}

.component-properties {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Visual Data Source Editor Styles */
.options-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.btn-add-option {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #25d366;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-option:hover {
  background: #20ba5a;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.2);
}

.btn-add-option svg {
  flex-shrink: 0;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: all 0.2s;
}

.option-item:hover {
  background: white;
  border-color: #d1d5db;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.option-drag-handle {
  cursor: grab;
  color: #9ca3af;
  font-size: 14px;
  line-height: 1;
  user-select: none;
  padding: 4px;
}

.option-drag-handle:active {
  cursor: grabbing;
}

.option-fields {
  display: flex;
  flex: 1;
  gap: 8px;
}

.form-control-sm {
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 12px;
  flex: 1;
  transition: all 0.2s;
  background: white;
}

.form-control-sm:focus {
  outline: none;
  border-color: #25d366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.form-control-sm::placeholder {
  color: #9ca3af;
  font-size: 11px;
}

.btn-remove-option {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.btn-remove-option:hover {
  background: #fee2e2;
  transform: scale(1.1);
}

.empty-options {
  padding: 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
  background: #f9fafb;
  border: 1px dashed #e5e7eb;
  border-radius: 6px;
  line-height: 1.5;
}

.quick-options {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}

.quick-options small {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
}

.btn-quick {
  padding: 4px 10px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
}

.btn-quick:hover {
  background: #f3f4f6;
  border-color: #25d366;
  color: #25d366;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.1);
}

.btn-quick:active {
  transform: translateY(0);
}

.btn-add-component {
  padding: 10px 16px;
  background: white;
  border: 2px dashed #e5e7eb;
  border-radius: 6px;
  color: #25d366;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add-component:hover {
  background: #f0fdf4;
  border-color: #25d366;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background: #25d366;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #20ba5a;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.json-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.json-viewer-container {
  background: #1e293b;
  border-radius: 12px;
  width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.json-viewer-header {
  padding: 16px 20px;
  border-bottom: 1px solid #334155;
  display: flex;
  align-items: center;
  gap: 12px;
}

.json-viewer-header h3 {
  margin: 0;
  flex: 1;
  font-size: 16px;
  color: white;
}

.json-viewer-header .btn-sm {
  padding: 6px 12px;
  font-size: 12px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.json-viewer-header .close-btn {
  color: #94a3b8;
}

.json-content {
  flex: 1;
  overflow: auto;
  padding: 20px;
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #e2e8f0;
  background: #0f172a;
}
</style>
